import { OPMCalculatorResultsResponse } from 'api';
import {
  largeCurrencyFormat,
  oneDecimalPercentFormatValidateFloatTrue,
  smallDecimalFormat,
  twoDecimalPercentFormat,
  yearsDecimalFormat,
} from 'common/formats/formats';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import {
  BLACK_SCHOLES_SECTION_TITLE,
  breakpointColumnHeader,
  BREAKPOINTS_TITLE,
  CALL_OPTION_VALUE_TITLE,
  CURRENT_EQUITY_VALUE_TITLE,
  D1_TITLE,
  D2_TITLE,
  EXERCISE_PRICE_TITLE,
  getOPMCalculatorLadderWorkbookKey,
  HEADER_ROW_CLASS_NAME,
  MATURITY_TITLE,
  NOT_APPLICABLE,
  RISK_FREE_RATE_TITLE,
  SIZE_OF_INCREMENT_TITLE,
  VALUE_OF_INCREMENT_TITLE,
  VOLATILITY_TITLE,
} from '../constants';
import { OPMCalculatorInputs } from '../types';
import { breakpointTotal, isInfinityBreakpoint } from './utils';

interface CreateLadderConfigParams {
  panelId: string;
  inputs: OPMCalculatorInputs;
  results: OPMCalculatorResultsResponse;
}

/**
 * Row order below the header row, matching the design. Index 1 is a section heading with no
 * values. The final row is the value of each increment.
 */
const ROW_TITLES = [
  BREAKPOINTS_TITLE,
  BLACK_SCHOLES_SECTION_TITLE,
  CURRENT_EQUITY_VALUE_TITLE,
  EXERCISE_PRICE_TITLE,
  RISK_FREE_RATE_TITLE,
  MATURITY_TITLE,
  VOLATILITY_TITLE,
  D1_TITLE,
  D2_TITLE,
  CALL_OPTION_VALUE_TITLE,
  SIZE_OF_INCREMENT_TITLE,
  VALUE_OF_INCREMENT_TITLE,
];

/**
 * 1-indexed spreadsheet rows. Row 1 is the header row the spreadsheet renders from
 * `rowConfig[0]`, so every title in {@link ROW_TITLES} sits one row lower than its index.
 */
const ROW = {
  HEADER: 1,
  BREAKPOINTS: 2,
  SECTION_HEADING: 3,
  CURRENT_EQUITY_VALUE: 4,
  EXERCISE_PRICE: 5,
  RISK_FREE_RATE: 6,
  MATURITY: 7,
  VOLATILITY: 8,
  D1: 9,
  D2: 10,
  CALL_OPTION_VALUE: 11,
  SIZE_OF_INCREMENT: 12,
  VALUE_OF_INCREMENT: 13,
};

/**
 * Black-Scholes ladder.
 *
 * The leading column is a synthetic zero-strike baseline: the service emits one distribution
 * row per breakpoint and starts its increment chain from the equity value itself, so the
 * "call at strike 0 equals the equity value" column has no counterpart in the response and is
 * built here. Every later column maps to `distribution[i]` / `breakpoints[i]`.
 *
 * Column letters address *content* columns only -- the row titles are rendered by the
 * spreadsheet's own titles column from `rowConfig[].value`, so the baseline is column A.
 */
const createLadderConfig = ({ panelId, inputs, results }: CreateLadderConfigParams) => {
  const { breakpoints, distribution } = results;
  // The baseline column plus one per breakpoint. The titles column is not a content column.
  const totalColumns = breakpoints.length + 1;
  const alphabet = alphabetGenerator([], totalColumns);

  const rowConfig = [
    {
      value: results.cap_table_name,
      className: HEADER_ROW_CLASS_NAME,
      gridType: 'string',
      ignoreRowCopy: true,
      largeHeader: true,
      readOnly: true,
    },
    ...ROW_TITLES.map(title => ({
      value: title,
      gridType: 'string',
      ignoreRowCopy: true,
      readOnly: true,
      // Styled like the result tables' `Total` rows: it is the ladder's bottom line.
      ...(title === VALUE_OF_INCREMENT_TITLE && { titleClassName: 'subtitle' }),
    })),
  ];

  const parser = () => {
    let cells: Record<string, unknown> = {};

    const readOnlyCell = (key: string, value: unknown, format?: unknown) => ({
      key,
      value,
      format,
      readOnly: true,
      gridType: format ? 'number' : 'string',
    });

    /**
     * Every content cell is written explicitly. `SpreadsheetConfig` spreads the row config
     * into any cell the parser omits, which would otherwise repeat the row title across the
     * whole row -- most visibly on the section-heading row, which has no values at all.
     */
    const columnCells = (column: string, header: string, values: Record<number, unknown>) => {
      cells = {
        ...cells,
        [`${column}${ROW.HEADER}`]: readOnlyCell(`${column}${ROW.HEADER}`, header),
        [`${column}${ROW.SECTION_HEADING}`]: readOnlyCell(`${column}${ROW.SECTION_HEADING}`, ''),
      };

      Object.entries(values).forEach(([row, cell]) => {
        cells = { ...cells, [`${column}${row}`]: cell };
      });
    };

    // Baseline column: strike 0, so the call is worth the whole equity value and there is no
    // d1/d2 and no increment.
    const baseline = alphabet[0];
    columnCells(baseline, '', {
      [ROW.BREAKPOINTS]: readOnlyCell(`${baseline}${ROW.BREAKPOINTS}`, 0, largeCurrencyFormat),
      [ROW.CURRENT_EQUITY_VALUE]: readOnlyCell(
        `${baseline}${ROW.CURRENT_EQUITY_VALUE}`,
        inputs.equityValue ?? 0,
        largeCurrencyFormat
      ),
      [ROW.EXERCISE_PRICE]: readOnlyCell(`${baseline}${ROW.EXERCISE_PRICE}`, 0, largeCurrencyFormat),
      [ROW.RISK_FREE_RATE]: readOnlyCell(
        `${baseline}${ROW.RISK_FREE_RATE}`,
        inputs.riskFreeRate ?? 0,
        twoDecimalPercentFormat({ validateFloat: true })
      ),
      [ROW.MATURITY]: readOnlyCell(`${baseline}${ROW.MATURITY}`, inputs.maturity ?? 0, {
        ...yearsDecimalFormat,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      [ROW.VOLATILITY]: readOnlyCell(
        `${baseline}${ROW.VOLATILITY}`,
        inputs.volatility ?? 0,
        oneDecimalPercentFormatValidateFloatTrue
      ),
      [ROW.D1]: readOnlyCell(`${baseline}${ROW.D1}`, NOT_APPLICABLE),
      [ROW.D2]: readOnlyCell(`${baseline}${ROW.D2}`, NOT_APPLICABLE),
      [ROW.CALL_OPTION_VALUE]: readOnlyCell(
        `${baseline}${ROW.CALL_OPTION_VALUE}`,
        inputs.equityValue ?? 0,
        largeCurrencyFormat
      ),
      [ROW.SIZE_OF_INCREMENT]: readOnlyCell(`${baseline}${ROW.SIZE_OF_INCREMENT}`, ''),
      [ROW.VALUE_OF_INCREMENT]: readOnlyCell(`${baseline}${ROW.VALUE_OF_INCREMENT}`, NOT_APPLICABLE),
    });

    breakpoints.forEach((breakpoint, index) => {
      const column = alphabet[index + 1];
      const slice = distribution[index];
      const total = breakpointTotal(breakpoint);
      const previousTotal = index === 0 ? 0 : breakpointTotal(breakpoints[index - 1]);
      const isInfinity = isInfinityBreakpoint(breakpoint);

      // The Infinity rung has no numeric strike, so its breakpoint / exercise / increment
      // cells stay textual rather than being coerced to a number.
      const totalCell = (key: string) =>
        isInfinity
          ? readOnlyCell(key, String(breakpoint.total))
          : readOnlyCell(key, total, largeCurrencyFormat);

      columnCells(column, breakpointColumnHeader(index), {
        [ROW.BREAKPOINTS]: totalCell(`${column}${ROW.BREAKPOINTS}`),
        [ROW.CURRENT_EQUITY_VALUE]: readOnlyCell(
          `${column}${ROW.CURRENT_EQUITY_VALUE}`,
          inputs.equityValue ?? 0,
          largeCurrencyFormat
        ),
        [ROW.EXERCISE_PRICE]: totalCell(`${column}${ROW.EXERCISE_PRICE}`),
        [ROW.RISK_FREE_RATE]: readOnlyCell(
          `${column}${ROW.RISK_FREE_RATE}`,
          inputs.riskFreeRate ?? 0,
          twoDecimalPercentFormat({ validateFloat: true })
        ),
        [ROW.MATURITY]: readOnlyCell(`${column}${ROW.MATURITY}`, inputs.maturity ?? 0, {
          ...yearsDecimalFormat,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        [ROW.VOLATILITY]: readOnlyCell(
          `${column}${ROW.VOLATILITY}`,
          inputs.volatility ?? 0,
          oneDecimalPercentFormatValidateFloatTrue
        ),
        // d1/d2 arrive as "N/A" for the zero-strike, Infinity and zero-volatility cases.
        [ROW.D1]: readOnlyCell(
          `${column}${ROW.D1}`,
          slice?.option?.d1 ?? NOT_APPLICABLE,
          Number.isFinite(Number(slice?.option?.d1)) ? smallDecimalFormat : undefined
        ),
        [ROW.D2]: readOnlyCell(
          `${column}${ROW.D2}`,
          slice?.option?.d2 ?? NOT_APPLICABLE,
          Number.isFinite(Number(slice?.option?.d2)) ? smallDecimalFormat : undefined
        ),
        [ROW.CALL_OPTION_VALUE]: readOnlyCell(
          `${column}${ROW.CALL_OPTION_VALUE}`,
          slice?.option_value ?? 0,
          largeCurrencyFormat
        ),
        [ROW.SIZE_OF_INCREMENT]: isInfinity
          ? readOnlyCell(`${column}${ROW.SIZE_OF_INCREMENT}`, String(breakpoint.total))
          : readOnlyCell(`${column}${ROW.SIZE_OF_INCREMENT}`, total - previousTotal, largeCurrencyFormat),
        [ROW.VALUE_OF_INCREMENT]: readOnlyCell(
          `${column}${ROW.VALUE_OF_INCREMENT}`,
          slice?.option_increment ?? 0,
          largeCurrencyFormat
        ),
      });
    });

    return cells;
  };

  return new SpreadsheetConfig({
    name: getOPMCalculatorLadderWorkbookKey(panelId),
    parser,
    rowConfig,
    columns: Array.from({ length: totalColumns }, () => ({})),
    tableData: { inputs, results },
    tableTerms: {
      tableName: results.cap_table_name,
      tableSlug: 'opm-calculator-ladder',
    },
    showToolbar: false,
    showTotalColumn: false,
    currencyFormatter: false,
    unitsFormatter: false,
    allowCopyColumn: false,
    allowReorderColumns: false,
    allowConfirmAndDeleteColumn: false,
  } as unknown as ConstructorParameters<typeof SpreadsheetConfig>[0]);
};

export default createLadderConfig;
